/**
 * Opening and closing a map in the engine (Phase 5b, step 4). Opening: the
 * last map closed (its unused attachments swept first), the map's key and
 * its registration on the server, loading it from this device and syncing
 * it, the updates of its format and notes, choosing its tree (a map just
 * created gets its first content), then the engine's map state
 * (engine/map-state.js). The interface shows the map after: engine.maps.open
 * for any interface; the classic one (interfaces/classic/managers/document-loader.js) sets its
 * screen state in `onOpened` and draws the view.
 *
 * Moved from the classic loader and interfaces/classic/managers/sync-orchestrator.js, same
 * steps. What the person should know goes through the host (engine/host.js):
 * notices, the notes migration's card. The progress goes to `onProgress`.
 *
 * While a map opens, remote changes wait (engine/sync/document-manager.js
 * isDocumentLoading) and the classic interface draws nothing.
 */
import * as CRDTBridge from "./sync/crdt-bridge.js";
import * as LinkAccess from "./sync/link-access.js";
import * as MapState from "./map-state.js";
import { VaultStorage } from "./crypto/vault-storage.js";
import { getCurrentUser } from "./account.js";
import { getOrCreateLocalUser } from "./auth/handlers.js";
import { emit, Events } from "./core/events.js";
import { storageOf } from "./core/vault-folders.js";
import { getStorageInfo } from "./services/storage-service.js";
import { recordQuotaRejection } from "./services/quota-state.js";
import {
  prepareDeviceMapKey, clearAttachmentCache, collectAttachmentRefsFromTree, sweepAttachments,
} from "./services/attachment-service.js";
import { isKnownOnline, markKnownOnline } from "./sync/known-online.js";
import { SCHEMA_V2 } from "./sync/map-format.js";
import { migrateOpenMapToV2 } from "./sync/map-format-migration.js";
import { dropOpenMapOldCopy } from "./sync/old-copy-drop.js";
import { scheduleBackgroundConversion } from "./sync/background-conversion.js";
import { ensureNotesSerializer } from "./sync/notes-serializer-facade.js";
import { isDocumentLoading, setIsDocumentLoading } from "./sync/document-manager.js";
import { notify, beginBlockingOperation } from "./host.js";
import { NOTICE } from "./notices.js";
import { t } from "../shared/i18n/index.js";

/**
 * Stages of an opening, for `onProgress(fraction, stage)`:
 * "opening", "unlocking", "loading", "reading-local", "local-loaded",
 * "fetching", "syncing", "building".
 */
const noProgress = () => {};

/** Where the open map is stored: 'online', 'local' or null (no vault entry). */
export function openMapStorage() {
  const docId = CRDTBridge.getCurrentDocId?.();
  const entry = docId && VaultStorage.getIndex?.()?.docs?.find((d) => d.docId === docId);
  return entry ? storageOf(entry) : null;
}

/**
 * Safe notes migration with backup, progress card, and rollback.
 *
 * Runs both node-notes and doc-notes migrations in sequence under a
 * single backup, so a failure in either path leaves a recoverable
 * Y.Doc snapshot in `orbital-migration-backups`. Both migrations are
 * idempotent (no-op when already in yxml-v1 mode), so this is cheap to
 * call on every doc open.
 */
async function safeNodeNotesMigration() {
  // The fragment migrations serialize markdown ↔ Y.XmlFragment through the
  // code-split TipTap chunk — make sure it's resident before converting.
  // Callers fire-and-forget this function, so a chunk-load failure must not
  // become an unhandled rejection: skip the migration (it's idempotent and
  // re-runs on the next doc open) instead of crashing.
  try {
    await ensureNotesSerializer();
  } catch (err) {
    console.warn("[Migration] Notes serializer unavailable, deferring migration:", err);
    return;
  }

  // The card, shown once there is something to convert.
  let waiting = null;
  const progress = (current, total) => {
    if (!waiting && current === 1 && total > 0) {
      waiting = beginBlockingOperation({
        title: t("account.migration.title"),
        intro: t("account.migration.intro"),
        preparing: t("account.migration.preparing"),
        done: t("account.migration.done"),
        failed: t("account.migration.failed"),
      });
    }
    waiting?.update?.(current, total);
  };

  try {
    // Create backup BEFORE either migration starts (outside any transaction).
    const backup = CRDTBridge.createDocBackup?.();
    if (backup) {
      try {
        const docId = CRDTBridge.getCurrentDocId?.();
        if (docId && typeof indexedDB !== 'undefined') {
          const req = indexedDB.open('orbital-migration-backups', 1);
          req.onupgradeneeded = (e) => e.target.result.createObjectStore('backups');
          req.onsuccess = (e) => {
            const db = e.target.result;
            const tx = db.transaction('backups', 'readwrite');
            tx.objectStore('backups').put(backup, docId);
          };
        }
      } catch (e) {
        console.warn('[Migration] Failed to save backup:', e);
      }
    }

    // Step 1: node notes (the original migration path).
    const nodeResult = CRDTBridge.migrateNodeNotes(progress);
    if (nodeResult?.migrated && nodeResult.count > 0) {
      console.log(`[Migration] Migrated ${nodeResult.count} node notes to Y.XmlFragment`);
    }

    // Step 2: doc-notes content → Y.XmlFragment (new, additive). Bridge
    // release: populates fragments without clearing legacy `content`
    // strings, so older clients keep reading them. Skipped if the
    // migrateDocNotesToFragments export is unavailable (defensive — the
    // version marker on the doc keeps subsequent calls idempotent).
    if (typeof CRDTBridge.migrateDocNotesToFragments === 'function') {
      const docResult = CRDTBridge.migrateDocNotesToFragments(progress);
      if (docResult?.migrated && docResult.count > 0) {
        console.log(`[Migration] Migrated ${docResult.count} doc-notes content to Y.XmlFragment`);
      }
    }

    // Step 3: legacy doc-note refnotes → execution entries (additive; the
    // refnotes stay for older clients). Cheap and idempotent; no card.
    if (typeof CRDTBridge.migrateRefnotesToExecution === 'function') {
      try {
        const execResult = CRDTBridge.migrateRefnotesToExecution();
        if (execResult?.migrated && execResult.count > 0) {
          console.log(`[Migration] Migrated ${execResult.count} refnotes into execution entries`);
        }
      } catch (e) {
        console.warn('[Migration] refnotes → execution migration failed:', e);
      }
    }

    // Both migrations succeeded — drop the backup.
    try {
      const docId = CRDTBridge.getCurrentDocId?.();
      if (docId && typeof indexedDB !== 'undefined') {
        const req = indexedDB.open('orbital-migration-backups', 1);
        req.onupgradeneeded = (e) => e.target.result.createObjectStore('backups');
        req.onsuccess = (e) => {
          const db = e.target.result;
          const tx = db.transaction('backups', 'readwrite');
          tx.objectStore('backups').delete(docId);
        };
      }
    } catch (e) { /* ignore cleanup errors */ }

    waiting?.finish(true);
  } catch (err) {
    console.error('[Migration] Notes migration failed:', err);
    waiting?.finish(false);
  }

  // Step 4: the map's format, to schema v2 (once synced, as the steps above);
  // then, once per session, the maps nobody opens.
  await migrateOpenMapToV2(CRDTBridge);
  scheduleBackgroundConversion({
    vault: VaultStorage,
    getCurrentDocId: () => CRDTBridge.getCurrentDocId?.(),
    getUserId: () => getCurrentUser()?.user_id || null,
  });
  // Step 5: two weeks after its conversion, the map's old copy is dropped
  // (docs/plans/2026-10-13-drop-schema-v1.md), then the map is compacted.
  void dropOpenMapOldCopy(CRDTBridge, { online: openMapStorage() !== "local" })
    .catch((err) => console.warn("[Migration] The map keeps its old copy for now:", err));
}

/** The notes' and format's updates, once the map is synced. */
function afterSync() {
  CRDTBridge.migrateDocNotes();
  safeNodeNotesMigration();
}

const startBridge = (docId, userId) => CRDTBridge.init(docId, {
  userId,
  onPresenceChange: (users) => emit(Events.PRESENCE_CHANGED, { users }),
  isDocumentLoading,
});

/**
 * Load the map from this device and start its sync ("CRDT first"): with a
 * copy on this device it is returned at once and syncs in the background;
 * without, the sync is waited for. Work that must wait for the sync goes in
 * CRDTBridge.registerPostSyncCallback.
 *
 * @param {string} docId
 * @param {CryptoKey|null} encryptionKey - null: a map on this device, no sync
 * @returns {Promise<{synced: boolean, hasData: boolean, treeData: Object|null, docNotes: Array|null}>}
 */
async function startMapSync(docId, encryptionKey, onProgress = noProgress) {
  // In Tauri local-only mode there's no signed-in user: the persistent local
  // user's id. WebSocket sync is gated separately on encryptionKey.
  const userId = getCurrentUser()?.user_id || getOrCreateLocalUser()?.user_id || null;
  if (!userId) {
    console.log("[CRDT] No user (logged-in or local) available, skipping CRDT init");
    return { synced: false, hasData: false, treeData: null, docNotes: null };
  }

  try {
    onProgress(0.5, "reading-local");
    await startBridge(docId, userId);

    const hasLocalData = CRDTBridge.hasData();
    onProgress(0.7, hasLocalData ? "local-loaded" : "fetching");

    if (hasLocalData) {
      // FAST PATH: a copy on this device — shown at once, synced in the
      // background; remote changes arrive as tree changes.
      console.log("[CRDT] Local data found, rendering immediately (offline-first)");
      const treeData = CRDTBridge.getTreeFromCRDT();
      const docNotes = CRDTBridge.getDocNotes();
      onProgress(0.85, "building");
      if (encryptionKey) {
        // Returns once the socket connects, not once synced.
        CRDTBridge.startSync(encryptionKey).catch((err) => {
          console.error("[CRDT] Background sync connection failed:", err);
        });
        // Only once SYNCED is reached (not on a timeout).
        CRDTBridge.registerPostSyncCallback(() => {
          console.log("[CRDT] Post-sync: running notes migration");
          afterSync();
        });
      }
      console.log("[CRDT] Returning local data, sync in background | docId:", docId, "| docNotes:", docNotes?.length || 0);
      return { synced: false, hasData: true, treeData, docNotes };
    }

    // SLOW PATH: no copy on this device (first open here): wait for the server.
    console.log("[CRDT] No local data, waiting for sync");
    let synced = false;
    if (encryptionKey) {
      onProgress(0.72, "syncing");
      const result = await CRDTBridge.startSyncAndWait(encryptionKey);
      synced = result.synced;
      onProgress(0.85, "building");
    }
    // Notes from old locations: now if synced, else once it is.
    if (synced) {
      afterSync();
    } else if (encryptionKey) {
      CRDTBridge.registerPostSyncCallback(() => {
        console.log("[CRDT] Post-sync: running deferred notes migration");
        afterSync();
      });
    }
    const hasData = CRDTBridge.hasData();
    const treeData = hasData ? CRDTBridge.getTreeFromCRDT() : null;
    const docNotes = hasData ? CRDTBridge.getDocNotes() : null;
    console.log("[CRDT] Sync complete for document:", docId, "| synced:", synced, "| hasData:", hasData, "| docNotes:", docNotes?.length || 0);
    return { synced, hasData, treeData, docNotes };
  } catch (err) {
    console.error("[CRDT] Failed to initialize sync:", err);
    return { synced: false, hasData: false, treeData: null, docNotes: null };
  }
}

// One map change at a time: an open or a close asked while one runs (the
// person clicks another map while one loads, a background reopen, another
// interface) waits for it. Interleaved, the later one closed the earlier
// one's map in the middle of its opening: its tree, or a new map's first
// content, went to the wrong map or nowhere (data review, 2026-10-21).
// Inside an open, the close is the unqueued one (closeMapNow).
let mapChanges = Promise.resolve();
function oneAtATime(work) {
  const run = mapChanges.then(work, work);
  mapChanges = run.then(() => {}, () => {});
  return run;
}

/**
 * Close the open map: its sync stopped (the shared maps it shows too), its
 * attachments' cache emptied. MAP_CLOSED tells the interface. Waits for an
 * open under way.
 */
export function closeMap() {
  return oneAtATime(closeMapNow);
}

async function closeMapNow() {
  const docId = CRDTBridge.getCurrentDocId?.() ?? null;
  clearAttachmentCache();
  await CRDTBridge.destroy();
  emit(Events.MAP_CLOSED, { docId });
  console.log("[CRDT] Sync stopped");
}

/**
 * Ensure a document is registered on the server via POST /api/docs.
 * Idempotent: 201 = new owner, 200 = already registered, 409 = another user owns it.
 * Pre-migration docs may exist only in client IndexedDB, not in server's doc_keys table.
 *
 * Hard 5s timeout via AbortController. Mobile reopens after a long background
 * suspension can find the OS network stack still suspended; without a timeout
 * the fetch sits in a kernel buffer indefinitely, blocking the opening and
 * making the "tap doc → nothing happens" symptom indistinguishable from a
 * frozen UI. Timeouts surface as 'timeout' so the caller can say so.
 *
 * It also sends the map's wrapped key and owner proof (docs/architecture/
 * sync.md, "Vault repair"). The key only fills a missing one on the server,
 * in SQL: a key rotated meanwhile is never replaced by this copy. Without a
 * key on the server, attachment uploads were refused (409 dek_stale) for
 * maps registered before keys came with the registration.
 *
 * @returns {Promise<'ok'|'gone'|'quota'|'conflict'|'auth_failure'|'timeout'|'error'>}
 */
async function ensureDocRegistered(docId) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);
  try {
    const resp = await fetch("/api/docs", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      // `known`: this device has seen it online. If the server no longer has
      // it (moved to another device, or deleted there), it answers 410 and
      // creates nothing: this device's copy never brings it back online.
      body: JSON.stringify({ doc_id: docId, known: isKnownOnline(docId),
        ...(await VaultStorage.registrationFields?.(docId)) }),
      signal: controller.signal,
    });
    if (resp.status === 410) return 'gone';
    if (resp.status === 413) { recordQuotaRejection(docId); return 'quota'; }
    if (resp.status === 409) return 'conflict';
    if (resp.status === 401 || resp.status === 403) return 'auth_failure';
    if (!resp.ok) return 'error';
    // Registered (or already ours — the endpoint is idempotent for the owner).
    markKnownOnline([{ docId, title: VaultStorage.getDoc?.(docId)?.title || '' }]);
    return 'ok';
  } catch (e) {
    if (e?.name === 'AbortError') {
      console.warn("[OpenMap] Doc registration timed out after 5s:", docId);
      return 'timeout';
    }
    console.warn("[OpenMap] Failed to register doc on server:", e);
    return 'error';
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Before another map opens, ask the server to sweep the open map's
 * attachment files no longer referenced (deleted with a note…): it frees
 * quota. Best effort. The server lets only the owner and admins sweep, keeps
 * files just uploaded, and checks the role.
 */
async function sweepClosingMap(nextDocId) {
  try {
    const prevDocId = MapState.get("currentDocId");
    const prevTree = MapState.get("data");
    // Require prevTree.id to be present: an empty-but-valid tree
    // ({ id, children: [] }) legitimately has no keeps and the server
    // will correctly prune orphans; a tree without an id is a partial
    // or failed load and we must NOT sweep — that could delete live
    // blobs whose references we never saw.
    const prevIsLocal = VaultStorage.getDoc?.(prevDocId)?._storage === "local";
    // Only from a state known to be complete: a tree loaded from cache
    // (offline, sync paused, unreadable updates) lacks attachments other
    // members added, and the sweep would delete them.
    const prevSynced = !!CRDTBridge.isDocFullySynced?.(prevDocId);
    const prevRole = VaultStorage.getDoc?.(prevDocId)?.role || "owner";
    // (A map open by its link is never this account's to sweep.)
    const canSweep = !LinkAccess.isLinkMap(prevDocId) && (prevRole === "owner" || prevRole === "admin");
    if (!(prevDocId && prevDocId !== nextDocId && prevTree?.id && !prevIsLocal && prevSynced && canSweep)) return;
    let prevDocNotes = CRDTBridge.getDocNotes?.() || null;
    // In collaborative doc-notes mode the array entry's plain `content`
    // string is only refreshed on editor blur: it would miss attachments
    // added since, and the sweep would delete them. Each note's fragment
    // gives the accurate set.
    if (prevDocNotes && CRDTBridge.isCollabDocNotesEnabled?.() && CRDTBridge.serializeDocNote) {
      prevDocNotes = prevDocNotes.map((note) => {
        try {
          const md = CRDTBridge.serializeDocNote(note.id);
          if (md && md !== note.content) return { ...note, content: md };
        } catch { /* keep array's content on failure */ }
        return note;
      });
    }
    const keep = collectAttachmentRefsFromTree(prevTree, prevDocNotes, CRDTBridge.getRoutineEvents?.() || []);
    await sweepAttachments(prevDocId, keep);
  } catch (err) {
    console.warn('[OpenMap] Attachment sweep skipped:', err);
  }
}

/**
 * Show the open map's links with what they link to. The tree opened raw
 * (links empty); the usual rebuild adds the linked maps' content and starts
 * the sync of those not loaded yet (each one rebuilds again when its content
 * arrives). Waiting for those syncs is not enough: a linked map already
 * loaded (the map opened again while its links were still loading) or a map
 * linked into itself starts none, and the links stayed empty until the next
 * change. Maps without links skip this.
 */
function showLinkedMaps() {
  const hasLink = (node) => !!node && (!!node.linkedDocId || (node.children || []).some(hasLink));
  try {
    if (hasLink(MapState.get("data"))) CRDTBridge.rebuildStoreFromCRDT("document-open");
  } catch (err) {
    console.warn("[OpenMap] showing linked maps failed:", err);
  }
}

/** Apply queued and recovered-map link migrations to the open map. */
async function applyStubMigrations() {
  try {
    await CRDTBridge.applyStubMigrations?.();
  } catch (err) {
    console.warn("[OpenMap] Link migration failed:", err);
  }
}

/** A map just created: its first content, from the vault's pending state. */
function writeFirstContent(tree, pendingState, userSetProjectDates) {
  console.log("[OpenMap] Syncing initial data to CRDT");
  // A map just created starts in schema v2 (no other copy exists).
  CRDTBridge.syncFullTree(tree, { schema: SCHEMA_V2 });
  if (userSetProjectDates) CRDTBridge.setMetadata("userSetProjectDates", userSetProjectDates);
  // Importers bring the journal with the tree; older paths left notes on
  // the root (setDocNotes ignores a string).
  const initialDocNotes = (Array.isArray(pendingState?.docNotes) && pendingState.docNotes.length > 0)
    ? pendingState.docNotes
    : (tree.notes && tree.notes.length > 0 ? tree.notes : null);
  if (initialDocNotes) CRDTBridge.setDocNotes(initialDocNotes);
  // Tag definitions and board columns from an importer, in the same
  // syncing window as the tree.
  if (Array.isArray(pendingState?.tagDefinitions) && pendingState.tagDefinitions.length > 0) {
    CRDTBridge.setTagDefinitions(pendingState.tagDefinitions);
  }
  if (Array.isArray(pendingState?.kanbanColumns) && pendingState.kanbanColumns.length > 0) {
    CRDTBridge.setMetadata("kanbanColumns", pendingState.kanbanColumns);
  }
  // Execution entries (Today lists) from a per-map import.
  if (Array.isArray(pendingState?.routines)) CRDTBridge.setRoutineEvents(pendingState.routines);
  if (Array.isArray(pendingState?.taskPostponements)) CRDTBridge.setTaskPostponementEvents(pendingState.taskPostponements);
  if (Array.isArray(pendingState?.execution) && pendingState.execution.length > 0
      && typeof CRDTBridge.setExecutionItems === "function") {
    CRDTBridge.setExecutionItems(pendingState.execution);
  }
}

/**
 * Signed out (Tauri local-only, or the session ended): from this device
 * only. A map just created gets its first content, as signed in.
 */
async function openSignedOut(docId, onProgress, onOpened) {
  onProgress(0.2, "reading-local");
  const pendingState = VaultStorage.getPendingInitialState(docId);
  await startBridge(docId, getOrCreateLocalUser().user_id);
  // Opening an existing map must not re-seed the board columns its people
  // may have curated: only the Todo / In Progress / Done tags, which cannot
  // be deleted or renamed, are added when missing.
  CRDTBridge.migrateRefnotesToExecution();
  CRDTBridge.ensureDefaultTags();
  await migrateOpenMapToV2(CRDTBridge);
  const isNew = !CRDTBridge.hasData() && !!pendingState?.data;
  const tree = isNew ? pendingState.data : CRDTBridge.getTreeFromCRDT();
  await prepareDeviceMapKey(docId);
  onProgress(0.8, "building");
  if (!tree) {
    console.error("[OpenMap] Failed to load doc data from CRDT");
    return null;
  }
  const userSetProjectDates = isNew
    ? pendingState.userSetProjectDates || false
    : CRDTBridge.getMetadata?.("userSetProjectDates") || false;
  const info = { mapId: docId, isLocal: true, isShared: false, shareRole: null, isNew, synced: false, userSetProjectDates };
  MapState.open(tree, { docId });
  onOpened(tree, info);
  if (isNew) {
    writeFirstContent(tree, pendingState, userSetProjectDates);
    CRDTBridge.migrateDocNotes();
  }
  VaultStorage.setCurrentDocId(docId);
  return info;
}

/**
 * Open a map; the open one is closed first.
 *
 * @param {string} docId
 * @param {Object} [options]
 * @param {(fraction: number, stage: string) => void} [options.onProgress]
 * @param {(tree: Object, info: Object) => void} [options.onOpened] - the
 *   tree is the engine's and the map still opening: an interface's screen
 *   state for it, before anything is drawn
 * @param {boolean} [options.afterSignIn] - the first map after signing in
 * @returns {Promise<{mapId: string, isLocal: boolean, isShared: boolean, shareRole: string|null,
 *   isNew: boolean, synced: boolean, userSetProjectDates: boolean}|null>} null when it did not open
 *   (a notice says why)
 */
export function openMap(docId, options = {}) {
  opensAsked += 1;
  return oneAtATime(() => openMapNow(docId, options));
}

// Opens asked since the page started, by any interface: the start's own
// choice of map after signing in (classic's loadInitialDoc) never replaces
// one opened meanwhile. Counted by classic's screens only until 2026-10-26:
// a map opened through the engine API right after signing in was replaced
// (the simulation, scripts/simulate-activity.mjs).
let opensAsked = 0;

/** How many opens were asked so far. */
export function opensAskedSoFar() {
  return opensAsked;
}

async function openMapNow(docId, { onProgress = noProgress, onOpened = noProgress, afterSignIn = false } = {}) {
  setIsDocumentLoading(true);
  try {
    // Signed out: from this device, except a map open by its link for this
    // page (link-access.js), which syncs like any other.
    if (!getCurrentUser() && !LinkAccess.pageMap(docId)) {
      await closeMapNow();
      return await openSignedOut(docId, onProgress, onOpened);
    }
    await sweepClosingMap(docId);
    await closeMapNow();
    onProgress(0.2, "opening");

    // Maps on this device skip the key, the registration and the sync: the
    // bridge loads them from the device's storage.
    const hasPendingState = VaultStorage.hasPendingInitialState(docId);
    const entry = VaultStorage.getDoc?.(docId);
    // Open by its link for this page only (link-access.js): its key is in
    // memory, not in the vault.
    const forPage = !entry && !!LinkAccess.pageMap(docId);
    const isLocal = entry?._storage === "local";
    const isOwned = !forPage && (!entry?.role || entry.role === "owner");

    let docKey = null;
    if (!isLocal) {
      try { await getStorageInfo({ force: true }); } catch { /* Keep known quota while offline. */ }
      // Keypair-based (falls back to the KEK for pre-migration maps).
      const dekBytes = forPage ? LinkAccess.pageMapKey(docId) : await VaultStorage.getDocDEK(docId);
      try {
        docKey = await crypto.subtle.importKey("raw", dekBytes, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
      } finally {
        if (forPage) dekBytes.fill(0);
      }
      onProgress(0.3, "unlocking");

      // H-4: registered before syncing (own maps only: a shared map is its
      // owner's, registering it answers 409).
      if (isOwned) {
        const registered = await ensureDocRegistered(docId);
        if (registered === 'gone') {
          emit(Events.MAPS_GONE_ONLINE, { docIds: [docId] });
          return null;
        }
        if (registered === 'conflict' && hasPendingState) {
          console.error("[OpenMap] Doc ID conflict (409): another user owns", docId);
          notify({ code: NOTICE.MAP_ID_CONFLICT, level: "error", message: t("app.load.idConflict"), data: { mapId: docId } });
          return null;
        }
        if (registered === 'auth_failure' && hasPendingState) {
          console.error("[OpenMap] Auth failure registering doc");
          return null;
        }
        // A timeout: the network stack is likely suspended (mobile after a
        // long time in the background) or the server unreachable. A map
        // with a copy here opens from it (the sync catches up later), and
        // the person is told it may be stale; a new map has no content yet:
        // it does not open.
        if (registered === 'timeout') {
          if (hasPendingState) {
            notify({ code: NOTICE.MAP_SERVER_UNREACHABLE, level: "error", message: t("app.load.serverUnreachable"), data: { mapId: docId } });
            return null;
          }
          notify({ code: NOTICE.MAP_SHOWING_CACHED, level: "warning", message: t("app.load.showingCached"), data: { mapId: docId } });
        }
      }
    } else {
      console.log("[OpenMap] Local-only doc, skipping DEK retrieval and sync:", docId);
    }
    // The pending state (a map just created), after the registration.
    onProgress(0.4, "loading");
    const pendingState = VaultStorage.getPendingInitialState(docId);

    const { synced, hasData, treeData: crdtTree, docNotes } = await startMapSync(docId, docKey, onProgress);
    // Maps on this device never reach the post-sync updates: the Todo / In
    // Progress / Done tags here (an import's own definitions, written below,
    // replace these by name), and the map format (schema v2: no other copy
    // to wait for; same tree before and after).
    if (isLocal) CRDTBridge.ensureDefaultTags?.();
    if (isLocal && hasData) {
      await migrateOpenMapToV2(CRDTBridge);
      // Two weeks after the conversion, its old copy (plan 2026-10-13-drop-schema-v1.md).
      await dropOpenMapOldCopy(CRDTBridge, { online: false })
        .catch((err) => console.warn("[OpenMap] The map keeps its old copy for now:", err));
    }

    let tree;
    let isNew = false;
    let userSetProjectDates = false;
    if (hasData && crdtTree) {
      tree = crdtTree;
      // Legacy refnotes into the execution map from the local data right
      // away: local-only and offline maps never reach SYNCED. Cheap and
      // idempotent.
      try {
        CRDTBridge.migrateRefnotesToExecution?.();
      } catch (err) {
        console.warn("[OpenMap] refnotes → execution reconciliation failed:", err);
      }
      // The journal from the map's own list when at least as complete.
      if (docNotes && docNotes.length > 0 && (!tree.notes || docNotes.length >= tree.notes.length)) {
        tree.notes = docNotes;
      }
      userSetProjectDates = CRDTBridge.getMetadata("userSetProjectDates") || false;
    } else if (pendingState) {
      console.log("[OpenMap] Initializing newly created map");
      tree = pendingState.data;
      isNew = true;
      userSetProjectDates = pendingState.userSetProjectDates || false;
    }
    if (!tree) {
      // Found nowhere (not synced before signing out…). Nothing is deleted:
      // an empty map is shown in its place.
      console.warn("[OpenMap] No data found for map:", docId, "- CRDT empty and no pending state. Creating empty placeholder.");
      notify({ code: NOTICE.MAP_CONTENT_MISSING, level: "warning", data: { mapId: docId, afterSignIn },
        message: t(afterSignIn ? "app.load.notSyncedBeforeLogout" : "app.load.contentNotLoaded") });
      tree = { id: docId, name: t("app.load.emptyMapName"), children: [], color: "#374151" };
    }

    // A map on this device: its attachments' key, before anything draws.
    if (isLocal) await prepareDeviceMapKey(docId);

    const isShared = !isOwned;
    const shareRole = entry?.role || (forPage ? LinkAccess.pageMap(docId)?.role : null) || (isOwned ? "owner" : null);
    // Open by its link, not as a member (kept, or for this page).
    const viaLink = !!entry?.link || forPage;
    const info = { mapId: docId, isLocal, isShared, shareRole, isNew, synced, userSetProjectDates, viaLink };
    MapState.open(tree, { docId, isShared, shareRole });
    onOpened(tree, info);
    // The one case for writing the whole tree: a map's first content.
    if (isNew) writeFirstContent(tree, pendingState, userSetProjectDates);

    // Remote changes that arrived meanwhile, then the links.
    setIsDocumentLoading(false);
    CRDTBridge.flushPendingRemoteUpdate();
    showLinkedMaps();
    // Links to maps revoked (and recovered) since this map was last open
    // now point at the recovered copies.
    await applyStubMigrations();
    // (A map open for this page only is not in the vault.)
    if (!forPage) VaultStorage.setCurrentDocId(docId);
    emit(Events.QUOTA_MAP_OPENED, { docId });
    return info;
  } finally {
    setIsDocumentLoading(false);
    // Remote changes deferred while opening, on any exit (idempotent).
    CRDTBridge.flushPendingRemoteUpdate();
  }
}
